import { makeAutoObservable, runInAction } from 'mobx';
import { SessionType } from '../../types/others';
import { Account, AccountProvider } from '../../data';
import Device from '../device';
import timeState from './time';
import { AccountResponse } from '../../data/models/account';

class MainState {
  _isTv = false;
  _session: SessionType | undefined = undefined;

  _account?: Account = undefined;
  _mediaToken?: string = undefined;
  _mediaExpired?: number = undefined;

  _settings?: AccountResponse['settings'] = undefined;
  _criticalError?: number = undefined;

  _catchUp?: { delay: number; length: number } = undefined;

  constructor() {
    makeAutoObservable(this, { _catchUp: false });
  }

  get isTv(): boolean {
    return this._isTv;
  }
  set isTv(value: boolean) {
    this._isTv = value;
  }

  get criticalError() {
    return this._criticalError;
  }
  set criticalError(value: number | undefined) {
    this._criticalError = value;
  }

  async initSession(sid: string, sidName?: string) {
    this._session = { sid, sidName: sidName ?? 'SSID' };
    try {
      const res = await AccountProvider.account();
      runInAction(() => {
        this._account = res.account;
        this._settings = res.settings;
        this._mediaToken = res.media_token;
        this._mediaExpired = res.media_status
          ? res.media_date_to ?? -1
          : undefined;
      });
      timeState.setTime(res.servertime, true);
    } catch (error) {
      runInAction(() => {
        this._session = undefined;
      });
      throw error;
    }
  }

  get session() {
    return this._session;
  }

  get account() {
    if (this._account === undefined) throw new Error('Account is undefined');
    return this._account;
  }

  get mediaToken() {
    if (this._mediaToken === undefined)
      throw new Error('Media token is undefined');
    return this._mediaToken;
  }

  get mediaExpired() {
    return this._mediaExpired;
  }

  get archiveParams() {
    if (this._catchUp) return this._catchUp;

    const catchUp = this._settings?.stream_standard.list.find(
      e => e.value === 'hls_h264',
    )?.catchup;
    if (catchUp === undefined)
      this._catchUp = {
        delay: 300,
        length: 1209480,
      };
    else
      this._catchUp = {
        delay: catchUp.delay,
        length: catchUp.length,
      };
    return this._catchUp;
  }

  get bufferLength() {
    return +(this._settings?.http_caching.value ?? 30000);
  }

  async login(login: string, password: string) {
    const res = await AccountProvider.login(
      login,
      password,
      Device.softId(),
      Device.getUuid(),
    );

    runInAction(() => {
      this._session = { sid: res.sid, sidName: res.sid_name };
      this._account = res.account;
      this._settings = res.settings;
      this._mediaToken = res.media_token;
      this._mediaExpired = res.media_status ? res.media_date_to! : undefined;
    });
  }

  async logout(dropOnly = false) {
    try {
      if (!dropOnly) await AccountProvider.logout();
    } finally {
      this._catchUp = undefined;
      runInAction(() => {
        this._session = undefined;
        this._account = undefined;
        this._mediaToken = undefined;
        this._settings = undefined;
      });
    }
  }

  async setBufferLength(value: number) {
    if (this._settings === undefined) return;
    await AccountProvider.setCaching(value);
    runInAction(() => {
      this._settings!.http_caching.value = value;
    });
  }
}

export default new MainState();
